import React, { useMemo, useState, useEffect } from 'react';
import { Container, Paper, Select, Tabs } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { TabType } from './types';
import { PhysiologicalsTab, QuestionnaireTab, JournalTab } from './components';
import classes from './tab.module.css';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import { PatientStore } from '../../../global/Store/PatientStore';
import { usePatientJournalEntries } from '../../../global/entities/Journal/queries/useJournalEntries';
import { useFeatureVisibility } from '../../../global/provider/featureVisibilityContext';

interface MedicalOverviewGraphProps {
    patientStore?: PatientStore;
}

// Format metrics for dropdown selection based on available physiological types
const MedicalOverviewGraph: React.FC<MedicalOverviewGraphProps> = ({ patientStore }) => {
    const [activeTab, setActiveTab] = useState<TabType>('questionnaires');
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const isSmallScreen = useMediaQuery('(max-width: 767px)');
    const { isFeatureVisible } = useFeatureVisibility();
    const showPhysiologicals = isFeatureVisible('physiologicals');

    // Check if patient has journal entries
    const patientId = patientStore?.patientId;
    const { data: journalEntries, isLoading: journalEntriesLoading } = usePatientJournalEntries(patientId ? parseInt(patientId) : 0);
    
    // Check if user has any completed journal entries
    const hasJournalData = useMemo(() => {
        return (journalEntries || []).some(entry => entry.isCompleted);
    }, [journalEntries]);
    
    // Handle tab switching when journal data availability changes
    useEffect(() => {
        if (activeTab === 'journals' && !hasJournalData && !journalEntriesLoading) {
            // If currently on journals tab but no journal data, switch to questionnaires
            setActiveTab('questionnaires');
        }
    }, [activeTab, hasJournalData, journalEntriesLoading]);

    const handleTabChange = (value: string | null) => {
        if (value === 'physiologicals' || value === 'questionnaires' || (value === 'journals' && hasJournalData)) 
            setActiveTab(value);
        
    };

    const selectOptions = useMemo(() => {
        const options = [
            { value: 'questionnaires', label: t('graph.questionnaires') },
        ];
        if (hasJournalData)
            options.push({ value: 'journals', label: t('graph.journals') });

        if (showPhysiologicals)
            options.push({ value: 'physiologicals', label: t('graph.physiologicals') });

        return options;
    }, [t, i18n.language, hasJournalData, showPhysiologicals]);

    const tabList = useMemo(() => {
        return (
            <Tabs.List>
                <Tabs.Tab value="questionnaires">{t('graph.questionnaires')}</Tabs.Tab>
                {hasJournalData && <Tabs.Tab value="journals">{t('graph.journals')}</Tabs.Tab>}
                {showPhysiologicals && <Tabs.Tab value="physiologicals">{t('graph.physiologicals')}</Tabs.Tab>}
            </Tabs.List>
        );
    }, [t, i18n.language, location, hasJournalData, showPhysiologicals]);

    const contentPanel = (
        <Paper p="md" shadow="xs" style={{ width: '100%', borderRadius: '0 0 10px 10px' }}>
            {activeTab === 'physiologicals' ? (
                <PhysiologicalsTab />
            ) : activeTab === 'questionnaires' ? (
                <QuestionnaireTab />
            ) : (
                <JournalTab />
            )}
        </Paper>
    );

    return (
        <Container fluid p={0} style={{ width: '100%', maxWidth: '100%' }}>
            {isSmallScreen ? (
                <div className={classes.mobileSelect}>
                    <Select
                        value={activeTab}
                        onChange={handleTabChange}
                        data={selectOptions}
                        allowDeselect={false}
                        comboboxProps={{ shadow: 'sm' }}
                    />
                </div>
            ) : (
                <Tabs defaultValue="settings" value={activeTab} onChange={handleTabChange} classNames={classes}>
                    {tabList}
                </Tabs>
            )}
            {contentPanel}
        </Container>
    );
};

export default inject('patientStore')(observer(MedicalOverviewGraph));
